import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSpinner, 
  faCheckCircle, 
  faTimesCircle, 
  faEnvelope,
  faArrowLeft,
  faHome
} from '@fortawesome/free-solid-svg-icons';
import config from '../config/config';
import './EmailVerificationPage.css';

const EmailVerificationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // State management
  const [verificationStatus, setVerificationStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  
  // Get token from URL parameters
  const token = searchParams.get('token');

  useEffect(() => {
    if (token) {
      verifyEmail(token);
    } else {
      setVerificationStatus('error');
      setError('No verification token provided in the URL.');
    }
  }, [token]);

  const verifyEmail = async (verificationToken) => {
    try {
      setVerificationStatus('loading');
      setError('');
      setMessage('Verifying your email address...');

      const response = await fetch(`${config.API_BASE_URL}/um/v1/auth/verify-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          verificationToken: verificationToken
        })
      });

      const result = await response.json();

      if (response.ok && result.status === 'success') {
        setVerificationStatus('success');
        setMessage(result.message || 'Email verified successfully! You can now log in to your account.');
      } else {
        setVerificationStatus('error');
        setError(result.message || 'Email verification failed. The token may be invalid or expired.');
      }
    } catch (err) {
      console.error('Error verifying email:', err);
      setVerificationStatus('error');
      setError('An unexpected error occurred while verifying your email. Please try again later.');
    }
  };

  const handleGoToLogin = () => {
    navigate('/login');
  };

  const handleGoHome = () => {
    navigate('/');
  };

  const handleResendVerification = () => {
    // This would typically navigate to a resend verification page
    // or show a form to enter email address
    alert('Please contact support to resend verification email.');
  };

  return (
    <div className="email-verification-container">
      <div className="verification-card">
        <div className="verification-header">
          <FontAwesomeIcon icon={faEnvelope} className="email-icon" />
          <h1>Email Verification</h1>
        </div>

        <div className="verification-content">
          {verificationStatus === 'loading' && (
            <div className="verification-loading">
              <FontAwesomeIcon icon={faSpinner} spin className="loading-spinner" />
              <h2>Verifying Email</h2>
              <p>{message}</p>
              <div className="loading-details">
                <p>Please wait while we verify your email address...</p>
                <p className="token-info">Token: {token}</p>
              </div>
            </div>
          )}

          {verificationStatus === 'success' && (
            <div className="verification-success">
              <FontAwesomeIcon icon={faCheckCircle} className="success-icon" />
              <h2>Email Verified Successfully!</h2>
              <p className="success-message">{message}</p>
              <div className="success-details">
                <p>Your email address has been verified and your account is now active.</p>
                <p>You can now log in and access all features of the platform.</p>
              </div>
              <div className="action-buttons">
                <button 
                  className="login-button"
                  onClick={handleGoToLogin}
                >
                  <FontAwesomeIcon icon={faArrowLeft} />
                  Go to Login
                </button>
                <button 
                  className="home-button"
                  onClick={handleGoHome}
                >
                  <FontAwesomeIcon icon={faHome} />
                  Go to Home
                </button>
              </div>
            </div>
          )}

          {verificationStatus === 'error' && (
            <div className="verification-error">
              <FontAwesomeIcon icon={faTimesCircle} className="error-icon" />
              <h2>Email Verification Failed</h2>
              <p className="error-message">{error}</p>
              <div className="error-details">
                <h3>Possible reasons:</h3>
                <ul>
                  <li>The verification link has expired</li>
                  <li>The verification token is invalid</li>
                  <li>The email has already been verified</li>
                  <li>There was a network connection issue</li>
                </ul>
              </div>
              <div className="action-buttons">
                <button 
                  className="retry-button"
                  onClick={() => token && verifyEmail(token)}
                >
                  <FontAwesomeIcon icon={faSpinner} />
                  Try Again
                </button>
                <button 
                  className="resend-button"
                  onClick={handleResendVerification}
                >
                  <FontAwesomeIcon icon={faEnvelope} />
                  Resend Verification
                </button>
                <button 
                  className="home-button"
                  onClick={handleGoHome}
                >
                  <FontAwesomeIcon icon={faHome} />
                  Go to Home
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="verification-footer">
          <p>Having trouble? <a href="mailto:support@example.com">Contact Support</a></p>
          <p className="timestamp">
            Verification attempted at: {new Date().toLocaleString()}
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationPage;
